import React, {
  HTMLAttributes,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Slider, { Settings } from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { SliderDots } from "../SliderDots/SliderDots";

export interface SliderContainerHandle {
  slickPrev: () => void;
  slickNext: () => void;
  slickPause(): void;
  slickPlay(): void;
}

interface ISliderContainerProps {
  settings?: Settings;
  nbSlide: number;
  dotsClass?: string;
  slideContentRef?: React.RefObject<HTMLDivElement>;
  activeSlide?: number;
  isHideDots?: boolean;
  onPlayAnalytic?: () => void;
  onSlideChange?: (index: number) => void;
  isAutoplay?: boolean;
  activeDot?: number;
}

export const SliderContainer = forwardRef<
  SliderContainerHandle,
  ISliderContainerProps & HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const {
    settings,
    nbSlide,
    dotsClass,
    slideContentRef,
    activeSlide,
    isHideDots,
    children,
    className,
    onPlayAnalytic,
    onSlideChange,
    activeDot,
    isAutoplay = true,
  } = props;
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideRef = useRef<Slider>(null);

  useImperativeHandle(
    ref,
    () => ({
      slickPrev: () => slideRef.current?.slickPrev?.(),
      slickNext: () => slideRef.current?.slickNext?.(),
      slickPause: () => slideRef.current?.slickPause?.(),
      slickPlay: () => slideRef.current?.slickPlay?.(),
    }),
    [],
  );

  const slideSetting = {
    afterChange: (current: number) => {
      setCurrentSlide(current);
      onSlideChange?.(current);
    },
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: nbSlide > 1,
    autoplay: isAutoplay,
    arrows: false,
    lazyLoad: "progressive" as const,
    ...settings,
  };

  // Notify parent of initial slide on mount
  useEffect(() => {
    onSlideChange?.(0);
  }, []);

  useEffect(() => {
    if (activeSlide !== undefined) {
      slideRef.current?.slickGoTo?.(activeSlide);
    }
  }, [activeSlide]);

  useEffect(() => {
    const onPlay = () => {
      onPlayAnalytic?.();
      slideRef?.current?.slickPlay?.();
    };
    const onPause = () => {
      slideRef?.current?.slickPause?.();
    };
    slideContentRef?.current?.addEventListener("touchstart", onPause);
    slideContentRef?.current?.addEventListener("touchend", onPlay);
    return () => {
      slideContentRef?.current?.removeEventListener("touchstart", onPause);
      slideContentRef?.current?.removeEventListener("touchend", onPlay);
    };
  }, []);

  return (
    <>
      <Slider {...slideSetting} className={className} ref={slideRef}>
        {children}
      </Slider>
      {!isHideDots && nbSlide > 1 && (
        <SliderDots
          listLength={nbSlide}
          activeDot={activeDot || activeSlide || currentSlide}
          className={dotsClass}
          isHideGamepad
        />
      )}
    </>
  );
});

SliderContainer.displayName = "SliderContainer";
